import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { autoPill } from "@/components/shared/Pill";

const RANGES = [["week", "Týden"], ["month", "Měsíc"], ["year", "Rok"]];
// Barvy zakázek v rozpadu — stejné pořadí jako v prototypu.
const DOTS = ["bg-brand", "bg-amber-500", "bg-success", "bg-muted-foreground"];
const hm = (h) => {
  const m = Math.max(0, Math.round((h || 0) * 60));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
};

// variant="band": bez karty, bílé na terakotovém pruhu úvodu dělníka na PC (246)
export default function HoursChart({ userId, variant }) {
  const band = variant === "band";
  const [range, setRange] = useState("week");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(-1);      // -1 = celé období
  const [err, setErr] = useState(false);

  // Bump po každé změně směny (check-in/out, uzavření dne) — MyShiftBlock vysílá
  // "pp-shift-changed" a graf se musí obnovit, jinak sloupec dneška lže.
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    const bump = () => setRefreshKey((k) => k + 1);
    window.addEventListener("pp-shift-changed", bump);
    return () => window.removeEventListener("pp-shift-changed", bump);
  }, []);

  // Skeleton a reset výběru jen při změně období — tichá obnova po směně nechá
  // starý graf stát, dokud nepřijdou nová data (žádné probliknutí).
  useEffect(() => { setData(null); setSel(-1); }, [range, userId]);

  useEffect(() => {
    let alive = true;
    setErr(false);
    api.get("/attendance/summary", { params: { range, ...(userId ? { user_id: userId } : {}) } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [range, userId, refreshKey]);

  if (err) return null;   // graf je doplněk — když se nenačte, stránka jede dál
  const buckets = data?.buckets || [];
  const max = Math.max(...buckets.map((b) => b.hours), 0.01);
  const picked = sel >= 0 ? buckets[sel] : null;
  const rows = picked ? picked.projects : (data?.projects || []);
  const narrow = range === "month";

  return (
    <div className={band ? "min-w-0" : "bg-card border border-border rounded-[20px] p-4 md:p-5"} data-testid="hours-chart">
      <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
        <div className="min-w-0">
          <h2 className={`font-bold tracking-tight ${band ? "font-heading text-lg" : "text-base md:text-lg"}`}>Odpracované hodiny</h2>
          <p className={`text-[13px] truncate ${band ? "text-white/75" : "text-muted-foreground"}`}>{data?.label || "…"}</p>
        </div>
        <div ref={autoPill} className={`inline-flex p-1 rounded-full shrink-0 ${band ? "bg-black/20" : "bg-secondary border border-border"}`} data-testid="hours-chart-ranges">
          {RANGES.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setRange(k)} data-testid={`hours-range-${k}`} data-pill-on={range === k || undefined}
              className={`h-9 px-3.5 rounded-full text-[13px] font-bold transition-colors ${range === k ? (band ? "bg-white text-[#a84d2b]" : "bg-card text-foreground shadow-sm") : (band ? "text-white/80 hover:text-white" : "text-muted-foreground")}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="flex items-baseline gap-3 mt-3 flex-wrap min-w-0">
        <span className={`font-extrabold tracking-tight tabular-nums leading-none ${band ? "font-heading text-[44px] xl:text-[52px]" : "text-[30px] md:text-[40px]"}`}>{data ? hm(data.total_hours) : "—"}</span>
        <span className={`text-[13px] ${band ? "text-white/80" : "text-muted-foreground"}`}>{data?.note}</span>
      </div>

      {!data ? (
        <div className={`h-[132px] mt-4 rounded-[12px] animate-pulse motion-reduce:animate-none ${band ? "bg-white/10" : "bg-secondary"}`} />
      ) : (
        <div className="mt-4 overflow-x-auto">
          <div className={`flex items-end ${narrow ? "gap-[3px] min-w-[520px]" : "gap-1.5 md:gap-2.5"}`}>
            {buckets.map((b, i) => {
              const on = sel === i;
              return (
                <button key={b.key} type="button" onClick={() => setSel(on ? -1 : i)} data-testid={`hours-bar-${i}`}
                  title={`${b.label} · ${hm(b.hours)}`}
                  className="flex-1 flex flex-col items-center gap-1 min-w-0 group">
                  {!narrow && <span className={`text-[11px] tabular-nums whitespace-nowrap ${on ? `font-extrabold ${band ? "text-white" : "text-foreground"}` : `font-semibold ${band ? "text-white/75" : "text-muted-foreground"}`}`}>{b.hours > 0 ? hm(b.hours) : "—"}</span>}
                  <span className="w-full rounded-t-[6px] transition-colors" style={{ height: `${Math.max(b.hours > 0 ? 6 : 3, Math.round((b.hours / max) * 96))}px` }}>
                    <span className={`block w-full h-full rounded-t-[6px] ${band ? (b.hours === 0 ? "bg-white/15" : on ? "bg-white" : "bg-white/55 group-hover:bg-white/80") : (b.hours === 0 ? "bg-secondary" : on ? "bg-brand" : "bg-brand/45 group-hover:bg-brand/70")}`} />
                  </span>
                  <span className={`text-[11px] whitespace-nowrap ${on ? `font-extrabold ${band ? "text-white" : "text-foreground"}` : band ? "text-white/75" : "text-muted-foreground"}`}>{b.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className={`mt-4 pt-3.5 border-t flex items-start justify-between gap-3 flex-wrap min-w-0 ${band ? "border-white/15" : "border-border"}`}>
          <span className={`text-[11px] font-bold uppercase tracking-[.1em] shrink-0 ${band ? "text-white/75" : "text-muted-foreground"}`}>
            Podle zakázek{picked ? ` · ${picked.label}` : ""}
          </span>
          <div className="flex flex-wrap gap-x-4 gap-y-2 min-w-0">
            {rows.map((r, i) => (
              <span key={r.project_id || i} className="inline-flex items-center gap-2 min-w-0">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${band ? ["bg-white", "bg-white/65", "bg-white/40", "bg-black/30"][i % 4] : DOTS[i % DOTS.length]}`} />
                <span className="text-[13px] truncate max-w-[200px]">{r.name}</span>
                <b className="text-[13px] tabular-nums whitespace-nowrap">{hm(r.hours)}</b>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
